import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ScanFace, Loader2, Check, Sparkles, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { FaceCrop } from '../components/FaceCrop';

// Every detected face across the gallery, as crops, so admins can put names to faces.
export default function FacesTab() {
  const { adminListFaces, adminNameFace, adminDetectFaces, getDirectory } = useAuth();
  const [faces, setFaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unnamed, setUnnamed] = useState(true);
  const [busy, setBusy] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [names, setNames] = useState([]);
  const [draft, setDraft] = useState({});

  const load = useCallback(() => { setLoading(true); adminListFaces({ unnamed }).then(setFaces).catch(() => setFaces([])).finally(() => setLoading(false)); }, [adminListFaces, unnamed]);
  useEffect(load, [load]);
  useEffect(() => { getDirectory().then((d) => setNames((d.members || d || []).map((m) => m.name).filter(Boolean))).catch(() => null); }, [getDirectory]);

  const name = async (f, value) => {
    const v = (value ?? draft[f.face_id] ?? '').trim();
    setBusy(f.face_id);
    try {
      await adminNameFace(f.photo_id, f.face_id, v);
      toast.success(v ? `Named ${v}.` : 'Name cleared.');
      setFaces((cur) => cur.map((x) => (x.face_id === f.face_id ? { ...x, name: v || null } : x)).filter((x) => !unnamed || !x.name));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the name.'); }
    setBusy(null);
  };
  const detect = async () => {
    setDetecting(true);
    try { const r = await adminDetectFaces(5); toast.success(`Analysed ${r.analysed} photo(s)${r.errors ? `, ${r.errors} failed` : ''}. ${r.remaining} photo(s) still to scan.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Face detection failed.'); }
    setDetecting(false);
  };

  return (
    <div data-testid="admin-faces-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h2 className="font-title text-xl flex items-center gap-2" style={{ color: 'var(--maroon)' }}><ScanFace size={18} /> Faces</h2>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" checked={unnamed} onChange={(e) => setUnnamed(e.target.checked)} data-testid="faces-unnamed-only" /> Unnamed only</label>
          <button onClick={detect} disabled={detecting} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded font-title text-sm disabled:opacity-60" style={{ background: 'var(--gold)', color: '#3a2c06' }} data-testid="faces-detect">{detecting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Detect faces in 5 more photos</button>
        </div>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--olive)' }}>Each tile is a face found in a gallery photo. Type a name (or accept the suggestion) — it is added to that photo's People so the member can be searched in the gallery.</p>
      {loading ? <p className="text-sm flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 size={15} className="animate-spin" /> Loading…</p>
        : faces.length === 0 ? <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="faces-empty">{unnamed ? 'Every detected face has a name. Run detection on more photos to find new ones.' : 'No faces detected yet — click “Detect faces”.'}</p>
        : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="faces-grid">
            {faces.map((f) => (
              <div key={f.face_id} className="bg-white rounded-lg border p-3 flex gap-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`face-card-${f.face_id}`}>
                <FaceCrop src={f.photo_url} box={f.box} size={88} testId={`face-crop-${f.face_id}`} />
                <div className="min-w-0 flex-1">
                  <Link to={`/admin/photos?photo=${f.photo_id}`} className="text-xs hover:underline block truncate" style={{ color: 'var(--navy)' }} title={f.photo_title}>{f.photo_title || 'Untitled photo'}</Link>
                  {f.name ? (
                    <div className="mt-1 flex items-center gap-1 text-sm" style={{ color: '#1c6b2c' }} data-testid={`face-name-${f.face_id}`}><Check size={14} /> {f.name} <button onClick={() => name(f, '')} className="ml-1 p-0.5 rounded hover:bg-black/5" title="Clear name" data-testid={`face-clear-${f.face_id}`}><X size={12} /></button></div>
                  ) : (
                    <>
                      <input list="faces-names" value={draft[f.face_id] ?? ''} onChange={(e) => setDraft({ ...draft, [f.face_id]: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') name(f); }} placeholder="Type a name…" className="mt-1 w-full border rounded px-2 py-1 text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid={`face-input-${f.face_id}`} />
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {f.suggested_name && <button onClick={() => name(f, f.suggested_name)} disabled={busy === f.face_id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-title" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid={`face-suggest-${f.face_id}`}><Sparkles size={11} /> {f.suggested_name}</button>}
                        <button onClick={() => name(f)} disabled={busy === f.face_id || !(draft[f.face_id] || '').trim()} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-title disabled:opacity-40" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid={`face-save-${f.face_id}`}>{busy === f.face_id ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Save</button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      <datalist id="faces-names">{names.map((n) => <option key={n} value={n} />)}</datalist>
    </div>
  );
}
